import {
  Component,
  Input,
  forwardRef,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
  OnChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  label: string;
  value: string | number | null;
  disabled?: boolean;
}

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholder: string = 'Select an option...';
  @Input() disabled: boolean = false;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() noResultsText: string = 'No results found';
  @Input() showClearButton: boolean = true;
  @Input() filterEnabled: boolean = true;
  /**
   * Cap how many matching rows are rendered. Large option lists (e.g. archived users)
   * freeze the UI if every row is mounted at once — search still runs over the full list.
   */
  @Input() maxVisibleOptions: number = 200;

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown', { static: false }) dropdown: ElementRef<HTMLDivElement>;

  isOpen: boolean = false;
  searchText: string = '';
  /** Full filtered match set (may be large). */
  filteredOptions: SearchableSelectOption[] = [];
  /** Slice actually rendered in the DOM. */
  visibleOptions: SearchableSelectOption[] = [];
  selectedValue: string | number | null = null;
  selectedLabel: string = '';

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.syncOptionsFromInput();
  }

  ngOnChanges() {
    this.syncOptionsFromInput();
  }

  get hasMoreMatches(): boolean {
    return this.filteredOptions.length > this.visibleOptions.length;
  }

  get moreMatchesHint(): string {
    const hidden = this.filteredOptions.length - this.visibleOptions.length;
    if (hidden <= 0) {
      return '';
    }
    return `Showing ${this.visibleOptions.length} of ${this.filteredOptions.length}. Type to narrow the list.`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  toggleDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchText = '';
      this.filterOptions();
      if (this.filterEnabled) {
        setTimeout(() => {
          if (this.searchInput) {
            this.searchInput.nativeElement.focus();
          }
        }, 0);
      }
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.searchText = '';
    // Keep filteredOptions in sync for next open, but avoid heavy work while closed.
    this.visibleOptions = [];
  }

  filterOptions() {
    const source = this.options || [];
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredOptions = source;
    } else {
      const searchLower = this.searchText.toLowerCase().trim();
      this.filteredOptions = source.filter(option =>
        this.optionSearchText(option).includes(searchLower)
      );
    }
    this.applyVisibleCap();
  }

  selectOption(option: SearchableSelectOption) {
    if (option.disabled) {
      return;
    }
    this.selectedValue = option.value;
    this.selectedLabel = option.label;
    this.onChange(option.value);
    this.onTouched();
    this.closeDropdown();
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.selectedValue = null;
    this.selectedLabel = '';
    this.onChange(null);
    this.onTouched();
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  trackByValue(_index: number, option: SearchableSelectOption): string | number | null {
    return option.value;
  }

  valuesEqual(a: string | number | null, b: string | number | null): boolean {
    if (a === null || a === undefined || b === null || b === undefined) {
      return a === b;
    }
    return String(a) === String(b);
  }

  updateSelectedLabel() {
    if (this.selectedValue !== null && this.selectedValue !== undefined) {
      const option = (this.options || []).find(opt => this.valuesEqual(opt.value, this.selectedValue));
      this.selectedLabel = option ? option.label : '';
    } else {
      this.selectedLabel = '';
      this.selectedValue = null;
    }
  }

  writeValue(value: any): void {
    this.selectedValue = value;
    this.updateSelectedLabel();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private syncOptionsFromInput(): void {
    this.updateSelectedLabel();
    if (this.isOpen) {
      this.filterOptions();
    } else if (this.options) {
      // Cheap baseline while closed — do not clone thousands of rows until open.
      this.filteredOptions = this.options;
      this.visibleOptions = [];
    }
  }

  private optionSearchText(option: SearchableSelectOption): string {
    return (option.label || '').replace(/<[^>]*>/g, ' ').toLowerCase();
  }

  private applyVisibleCap(): void {
    const cap = Math.max(1, Number(this.maxVisibleOptions) || 200);
    if (this.filteredOptions.length <= cap) {
      this.visibleOptions = this.filteredOptions;
    } else {
      this.visibleOptions = this.filteredOptions.slice(0, cap);
    }
  }
}
